import React, { useState } from 'react'
import styles from './MicrositeRepeatableRows.module.css'
import { Plus, Trash2, GripVertical, ChevronDown, ChevronUp } from 'lucide-react'

/**
 * MicrositeRepeatableRows
 *
 * Generic add / remove / reorder repeater for array-shaped data —
 * powers testimonials, FAQs, team members, stats, features, nav
 * links, working hours, slides, and more across the microsite module.
 *
 * The actual field inputs for each item are supplied by the caller
 * via `renderItem`, so this component stays content-agnostic.
 *
 * Usage:
 *   <MicrositeRepeatableRows
 *     items={form.faqs}
 *     onChange={(next) => updateField('faqs', next)}
 *     emptyItem={{ q: '', a: '' }}
 *     addLabel="Add FAQ"
 *     renderItem={(item, idx, updateItem) => (
 *       <>
 *         <TextInput label="Question" value={item.q} onChange={(v) => updateItem('q', v)} />
 *         <TextInput label="Answer" value={item.a} onChange={(v) => updateItem('a', v)} />
 *       </>
 *     )}
 *     renderSummary={(item) => item.q || 'New FAQ'}
 *   />
 */
const MicrositeRepeatableRows = ({
    items = [],
    onChange,
    emptyItem = {},
    addLabel = 'Add item',
    renderItem,
    renderSummary,
    minItems = 0,
    maxItems = null,
    collapsible = true
}) => {
    const [expandedIdx, setExpandedIdx] = useState(collapsible ? null : -1)
    const [dragIdx, setDragIdx] = useState(null)

    const handleAdd = () => {
        if (maxItems && items.length >= maxItems) return
        const next = [...items, { ...emptyItem }]
        onChange?.(next)
        if (collapsible) setExpandedIdx(next.length - 1)
    }

    const handleRemove = (idx) => {
        if (items.length <= minItems) return
        const next = items.filter((_, i) => i !== idx)
        onChange?.(next)
        if (expandedIdx === idx) setExpandedIdx(null)
    }

    const handleUpdateItem = (idx, field, value) => {
        const next = items.map((item, i) =>
            i === idx ? { ...item, [field]: value } : item
        )
        onChange?.(next)
    }

    const toggleExpanded = (idx) => {
        if (!collapsible) return
        setExpandedIdx((prev) => (prev === idx ? null : idx))
    }

    // ── Drag to reorder ──
    const handleDragStart = (idx) => setDragIdx(idx)

    const handleDragOver = (e, idx) => {
        e.preventDefault()
        if (dragIdx === null || dragIdx === idx) return
    }

    const handleDrop = (idx) => {
        if (dragIdx === null || dragIdx === idx) {
            setDragIdx(null)
            return
        }
        const next = [...items]
        const [moved] = next.splice(dragIdx, 1)
        next.splice(idx, 0, moved)
        onChange?.(next)
        setDragIdx(null)
        setExpandedIdx(null)
    }

    const isExpanded = (idx) => !collapsible || expandedIdx === idx

    return (
        <div className={styles.wrap}>
            {items.length === 0 && (
                <div className={styles.emptyState}>No items yet — add your first one below.</div>
            )}

            <div className={styles.list}>
                {items.map((item, idx) => (
                    <div
                        key={idx}
                        className={`${styles.itemCard} ${dragIdx === idx ? styles.itemDragging : ''}`}
                        draggable
                        onDragStart={() => handleDragStart(idx)}
                        onDragOver={(e) => handleDragOver(e, idx)}
                        onDrop={() => handleDrop(idx)}
                    >
                        <div className={styles.itemHeader}>
                            <GripVertical size={14} className={styles.dragHandle} />

                            <button
                                type="button"
                                className={styles.itemSummaryBtn}
                                onClick={() => toggleExpanded(idx)}
                                disabled={!collapsible}
                            >
                                <span className={styles.itemIndex}>#{idx + 1}</span>
                                <span className={styles.itemSummaryText}>
                                    {renderSummary ? renderSummary(item, idx) : `Item ${idx + 1}`}
                                </span>
                            </button>

                            <div className={styles.itemHeaderActions}>
                                {collapsible && (
                                    <button
                                        type="button"
                                        className={styles.iconBtn}
                                        onClick={() => toggleExpanded(idx)}
                                        title={isExpanded(idx) ? 'Collapse' : 'Expand'}
                                    >
                                        {isExpanded(idx) ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className={styles.iconBtnDanger}
                                    onClick={() => handleRemove(idx)}
                                    disabled={items.length <= minItems}
                                    title="Remove"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </div>

                        {isExpanded(idx) && (
                            <div className={styles.itemBody}>
                                {renderItem(item, idx, (field, value) => handleUpdateItem(idx, field, value))}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <button
                type="button"
                className={styles.addBtn}
                onClick={handleAdd}
                disabled={maxItems !== null && items.length >= maxItems}
            >
                <Plus size={14} />
                {addLabel}
            </button>
        </div>
    )
}

export default MicrositeRepeatableRows